import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, delay, mergeMap, retryWhen } from 'rxjs/operators';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { MAX_QUERY_RESULTS, Query } from '@damofire/data-commons';
import { COMMONS_ENV } from './injection-tokens';
import { Auth } from '@angular/fire/auth';

@Injectable()
export class HttpService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(COMMONS_ENV);
  private readonly auth = inject(Auth);

  static gridQueryToUrlQueryParam(query: Query): string {
    let queryStr = '';
    const { from, size, searchPhrase, sort, filters, start, end } = query;
    queryStr += `from=${from || 0}`;
    queryStr += `&size=${size || 25}`;
    if (searchPhrase) {
      queryStr += `&searchPhrase=${searchPhrase}`;
    }
    if (sort) {
      const sortProps = Object.keys(sort);
      sortProps.forEach((p) => (queryStr += `&sort=${p}::${sort[p]}`));
    }
    if (filters) {
      const filterProps = Object.keys(filters);
      filterProps.forEach((p) => (queryStr += this.appendFilter(p, filters[p])));
    }
    if (start && end) {
      queryStr += `&start=${start}&end=${end}`;
    }
    return queryStr;
  }

  private static appendFilter(key: string, value: unknown | unknown[]): string {
    let queryStr = '';
    if (Array.isArray(value)) {
      value.forEach((subParam) => (queryStr += this.appendFilter(key, subParam)));
    } else {
      queryStr += `&filters=${key}::${value}`;
    }
    return queryStr;
  }

  private static handleResponseError<R>(error: HttpErrorResponse): Observable<R> {
    switch (error.status) {
      case 404: {
        return EMPTY;
      }
      case 403:
      case 500:
      default: {
        return throwError(() => error);
      }
    }
  }

  private static getApiUrl(baseUrl: string, ctxPath: string): string {
    return `${baseUrl}/${ctxPath}`.replace(/\/?$/, '/');
  }

  private async getAuthToken(noAuth = false): Promise<string> {
    if (noAuth) {
      return '';
    }
    const authCurrentUser = this.auth.currentUser;
    if (!authCurrentUser) {
      throw new Error(`No user when calling http cloud function.`);
    }
    const userToken = await authCurrentUser.getIdToken();
    if (!userToken) {
      throw new Error(`No user token when calling http cloud function.`);
    }
    return `Bearer ${userToken}`;
  }

  private static retry<T>() {
    const MAX_RETRY = 3;
    let retries = 0;
    return (src: Observable<T>) =>
      src.pipe(
        retryWhen((errors: Observable<HttpErrorResponse>) =>
          errors.pipe(
            mergeMap((error) => {
              if (error.status >= 500) {
                retries++;
                if (retries < MAX_RETRY) {
                  const delayMs = retries * 1000;
                  console.warn(`An error occurred, retry ${retries}/${MAX_RETRY} in ${delayMs}ms`);
                  return of(error).pipe(delay(delayMs));
                }
                return throwError(() => new Error('Max retry attempts exceeded'));
              } else {
                return throwError(() => error);
              }
            }),
          ),
        ),
      );
  }

  private async buildHeaders(noAuth = false, extraHeaders?: Record<string, string>): Promise<HttpHeaders> {
    const headers: Record<string, string> = {};
    const authHeader = await this.getAuthToken(noAuth);
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    if (extraHeaders) {
      Object.assign(headers, extraHeaders);
    }
    return new HttpHeaders(headers);
  }

  async post<R = any>(ctxPath: string, body?: {}, params?: HttpParams, noAuth = false): Promise<R> {
    const headers = await this.buildHeaders(noAuth);
    return this.http
      .post<R>(HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath), body, {
        responseType: 'json',
        params: params,
        headers,
      })
      .pipe(HttpService.retry<R>())
      .toPromise() as Promise<R>;
  }

  async delete<R = any>(ctxPath: string, params?: HttpParams): Promise<R> {
    const headers = await this.buildHeaders();
    return this.http
      .delete<R>(HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath), {
        responseType: 'json',
        params: params,
        headers,
      })
      .pipe(HttpService.retry<R>())
      .toPromise() as Promise<R>;
  }

  async patch<R>(ctxPath: string, body: {}, params?: HttpParams): Promise<R> {
    const headers = await this.buildHeaders();
    return this.http
      .patch<R>(HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath), body, {
        responseType: 'json',
        params: params,
        headers,
      })
      .pipe(HttpService.retry<R>())
      .toPromise() as Promise<R>;
  }

  async put<R>(ctxPath: string, body: {}, params?: HttpParams): Promise<R> {
    const headers = await this.buildHeaders();
    return this.http
      .put<R>(HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath), body, {
        responseType: 'json',
        params: params,
        headers,
      })
      .pipe(HttpService.retry<R>())
      .toPromise() as Promise<R>;
  }

  async get<R>(ctxPath: string, params?: HttpParams, noAuth = false): Promise<R> {
    const url = HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath);
    const headers = await this.buildHeaders(noAuth);
    const response = this.http
      .get<R>(url, {
        responseType: 'json',
        params: params,
        headers,
      })
      .pipe(
        HttpService.retry<R>(),
        catchError((error) => HttpService.handleResponseError<R>(error)),
      );
    return response.toPromise() as Promise<R>;
  }

  async getRaw(ctxPath: string, params?: HttpParams, responseType: 'blob' | 'text' = 'text'): Promise<string> {
    const url = HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath);
    const headers = await this.buildHeaders();
    return this.http
      .get(url, {
        responseType: responseType as any,
        params: params,
        headers,
      })
      .pipe(HttpService.retry<any>())
      .toPromise() as Promise<string>;
  }

  async getByQueryCsv(ctxPath: string, query: Query, params?: HttpParams): Promise<string> {
    query.from = 0;
    query.size = MAX_QUERY_RESULTS;
    const queryStr = HttpService.gridQueryToUrlQueryParam(query);
    const url = HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath);
    const headers = await this.buildHeaders(false, { 'Content-Type': 'text/csv' });
    return this.http
      .get<string>(`${url}?${queryStr}`, {
        responseType: 'text' as any,
        params: params,
        headers,
      })
      .toPromise() as Promise<string>;
  }

  async getByQuery<R>(ctxPath: string, query: Query, params?: HttpParams): Promise<R> {
    const queryStr = HttpService.gridQueryToUrlQueryParam(query);
    const url = HttpService.getApiUrl(this.environment.appConfig.fns, ctxPath);
    const headers = await this.buildHeaders();
    return this.http
      .get<R>(`${url}?${queryStr}`, {
        responseType: 'json',
        params: params,
        headers,
      })
      .pipe(
        HttpService.retry<R>(),
        catchError((error) => HttpService.handleResponseError<R>(error)),
      )
      .toPromise() as Promise<R>;
  }
}
